import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import AccountSettings from './AccountSettings';
import AnnouncementApprovalModal from './AnnouncementApprovalModal';
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from '../api/apiClient';
import socket from '../socket';
import './Navigation.css';

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const TYPE_ICON = {
  new_announcement:    '📢',
  comment_on_post:     '💬',
  reply_to_comment:    '↩️',
  mention:             '@',
  reaction:            '👍',
  announcement_pending: '🕓',
  announcement_approved: '✅',
  announcement_denied:   '⛔',
};

const REVIEW_TYPES = new Set(['announcement_pending', 'announcement_approved', 'announcement_denied']);

const Navigation = ({ currentUser, onSignIn, onSignOut, onUserUpdate, isAuthenticated, loadingUser }) => {
  const [dropdownOpen,  setDropdownOpen]  = useState(false);
  const [showAccount,   setShowAccount]   = useState(false);
  const [notifOpen,     setNotifOpen]     = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [reviewModalId, setReviewModalId] = useState(null);
  const [navHidden,     setNavHidden]     = useState(false);
  const [navHeight,     setNavHeight]     = useState(0);
  const dropdownRef  = useRef(null);
  const notifRef     = useRef(null);
  const navRef       = useRef(null);
  const lastScrollY  = useRef(0);
  const navigate    = useNavigate();
  const location    = useLocation();

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const fetchNotifications = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const res = await getNotifications(currentUser.id);
      setNotifications(res.data || []);
    } catch {}
  }, [currentUser?.id]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!currentUser?.id) return;
    const handler = (recipientId) => {
      if (recipientId === currentUser.id) {
        fetchNotifications().then(() => {
          // Show browser notification if enabled
          if (Notification.permission === 'granted') {
            const latestNotif = notifications[0];
            if (latestNotif && !latestNotif.is_read) {
              new Notification('Esbrook Intranet', {
                body: latestNotif.message,
                icon: '/logos/esbrook_pc_logo_web_no_bg_black.png',
                tag: 'intranet-notification'
              });
            }
          }
        });
      }
    };
    socket.on('notifications:changed', handler);
    return () => socket.off('notifications:changed', handler);
  }, [currentUser?.id, fetchNotifications, notifications]);

  // Close user dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  // Close notif panel on outside click
  useEffect(() => {
    if (!notifOpen) return;
    const handler = e => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [notifOpen]);

  const handleNotifClick = async (notif) => {
    if (!notif.is_read) {
      await markNotificationRead(notif.id, currentUser.id);
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: 1 } : n));
    }
    if (notif.announcement_id) {
      setNotifOpen(false);
      if (REVIEW_TYPES.has(notif.type)) {
        setReviewModalId(notif.announcement_id);
      } else {
        navigate('/announcements', {
          state: { focusAnnouncementId: notif.announcement_id, focusCommentId: notif.comment_id || null },
        });
      }
    }
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead(currentUser.id);
    setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
  };

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setNavHeight(el.offsetHeight));
    ro.observe(el);
    setNavHeight(el.offsetHeight);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (window.innerWidth > 768) return;
      const y = window.scrollY;
      if (y > lastScrollY.current && y > 60) {
        setNavHidden(true);
        setNotifOpen(false);
        setDropdownOpen(false);
      } else if (y < lastScrollY.current) {
        setNavHidden(false);
      }
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const openAccount = () => { setDropdownOpen(false); setShowAccount(true); };
  const openAdmin   = () => { setDropdownOpen(false); navigate('/admin'); };
  const handleSignOut = () => { setDropdownOpen(false); onSignOut?.(); };

  const initials = currentUser?.name
    ? currentUser.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  const isHome = location.pathname === '/';
  const goHome = () => {
    if (location.pathname === '/') window.scrollTo({ top: 0, behavior: 'smooth' });
    else navigate('/');
  };
  const isDirectory = location.pathname.startsWith('/directory');

  return (
    <>
      <nav ref={navRef} className={`navbar${navHidden ? ' navbar--hidden' : ''}`}>
        <div className="nav-container">
          <div
            className="nav-brand nav-brand--link"
            role="button"
            tabIndex={0}
            title="Home"
            onClick={goHome}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goHome(); } }}
          >
            <p>Firm Intranet</p>
            <img src="/logos/esbrook_pc_logo_web_no_bg_white.png" alt="Esbrook P.C." className="nav-brand-logo" />
          </div>

          <span className="nav-divider" aria-hidden="true" />

          <div className="nav-links">
            <button
              className={`nav-link nav-link--btn${isHome ? ' nav-link--active' : ''}`}
              onClick={goHome}
            >Home</button>
            <Link to="/directory" className={`nav-link${isDirectory ? ' nav-link--active' : ''}`}>Firm Directory</Link>
          </div>

          <span className="nav-divider" aria-hidden="true" />

          <div className="nav-user">
            {isAuthenticated && currentUser?.name ? (
              <>
                {/* Notification Bell */}
                <div className="notif-wrap" ref={notifRef}>
                  <button
                    className="notif-bell"
                    onClick={() => setNotifOpen(o => !o)}
                    aria-label="Notifications"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                    </svg>
                    {unreadCount > 0 && (
                      <span className="notif-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                    )}
                  </button>

                  {notifOpen && (
                    <div className="notif-dropdown">
                      <div className="notif-header">
                        <span className="notif-title">Notifications</span>
                        {unreadCount > 0 && (
                          <button className="notif-mark-all" onClick={handleMarkAllRead}>
                            Mark all read
                          </button>
                        )}
                      </div>
                      <div className="notif-list">
                        {notifications.length === 0 ? (
                          <p className="notif-empty">No notifications yet.</p>
                        ) : (
                          notifications.slice(0, 20).map(n => (
                            <div
                              key={n.id}
                              className={`notif-item ${n.is_read ? '' : 'notif-item--unread'}`}
                              onClick={() => handleNotifClick(n)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={e => e.key === 'Enter' && handleNotifClick(n)}
                            >
                              <span className="notif-icon">{TYPE_ICON[n.type] || '🔔'}</span>
                              <div className="notif-body">
                                <p className="notif-msg">{n.message}</p>
                                <span className="notif-time">{timeAgo(n.created_at)}</span>
                              </div>
                              {!n.is_read && <span className="notif-dot" />}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* User menu */}
                <div className="user-menu-wrap" ref={dropdownRef}>
                  <button
                    className="user-menu-trigger"
                    onClick={() => setDropdownOpen(o => !o)}
                    aria-expanded={dropdownOpen}
                  >
                    <div className="user-avatar">
                      {currentUser.avatar
                        ? <img src={currentUser.avatar} alt={currentUser.name} />
                        : initials}
                    </div>
                    <span className="user-name">{currentUser.name}</span>
                    <span className={`user-caret ${dropdownOpen ? 'open' : ''}`}>▾</span>
                  </button>

                  {dropdownOpen && (
                    <div className="user-dropdown">
                      <div className="dropdown-user-info">
                        <div className="dropdown-avatar">
                          {currentUser.avatar
                            ? <img src={currentUser.avatar} alt={currentUser.name} />
                            : initials}
                        </div>
                        <div>
                          <p className="dropdown-name">{currentUser.name}</p>
                          {currentUser.isAdmin && <p className="dropdown-role">Admin</p>}
                        </div>
                      </div>
                      <div className="dropdown-divider" />
                      <button className="dropdown-item" onClick={openAccount}>
                        <span className="dropdown-icon">⚙</span> Account Settings
                      </button>
                      {currentUser.isMythical && (
                        <button className="dropdown-item" onClick={openAdmin}>
                          <span className="dropdown-icon">🛡</span> Admin Settings
                        </button>
                      )}
                      <button className="dropdown-item" onClick={() => { setDropdownOpen(false); navigate('/crm'); }}>
                        <span className="dropdown-icon">◈</span> CRM
                      </button>
                      {currentUser.isMythical && (
                        <button className="dropdown-item" onClick={() => { setDropdownOpen(false); navigate('/crm/email'); }}>
                          <span className="dropdown-icon">✉</span> Email (Admin Only)
                        </button>
                      )}
                      <div className="dropdown-divider" />
                      <button className="dropdown-item dropdown-signout" onClick={handleSignOut}>
                        <span className="dropdown-icon">→</span> Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : !isAuthenticated && onSignIn ? (
              <button className="auth-button" onClick={onSignIn} disabled={loadingUser}>
                {loadingUser ? 'Loading…' : 'Sign in'}
              </button>
            ) : null}
          </div>
        </div>
      </nav>

      <div className="nav-spacer" style={{ height: navHeight }} aria-hidden="true" />

      {showAccount && (
        <AccountSettings
          currentUser={currentUser}
          onClose={() => setShowAccount(false)}
          onUserUpdate={onUserUpdate}
        />
      )}

      {reviewModalId && (
        <AnnouncementApprovalModal
          announcementId={reviewModalId}
          currentUser={currentUser}
          onClose={() => setReviewModalId(null)}
        />
      )}
    </>
  );
};

export default Navigation;
